import { Modal, Col, Row } from 'antd';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { useEffect, useState } from 'react';
import { BsCheckLg } from 'react-icons/bs';
import { FaRegListAlt } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import IntlMessages from '../../../../../../domain/utility/IntlMessages';
import Button from '../../../../../../shared/components/button';
import { productAttributesService } from '../../../../../../shared/services/product-attribute';
import {
  CreatePredefinedNewValueDto,
  PredefinedProductAttributeValuesDto,
} from '../../../../../../shared/services/product-attribute/dto/predefined-product-attribute-values-dto';
import '../../style.less';
import CreateOrEditPredefinedValue from './create-or-edit-predefined-value';
import { schema } from './schema';
import { getLocaleValue } from 'src/shared/functions/change-locales';
import LanguageCode from 'src/shared/services/types/language-code';
import MainUtils from 'src/shared/utils/main';

interface IProductAttributeModalProps {
  visible: boolean;
  productAttributeType: string;
  predefinedValueDetails?: PredefinedProductAttributeValuesDto;
  onCancel: () => void;
  onFinish: () => void;
}

const ProductAttributeModal = (props: IProductAttributeModalProps) => {
  const {
    productAttributeType,
    onCancel,
    visible,
    predefinedValueDetails,
    onFinish,
  } = props;

  const { messages } = useIntl();

  const { id } = useParams();

  const [initialValuesForm, setInitialValuesForm] =
    useState<CreatePredefinedNewValueDto>({
      name: '',
      priceAdjustment: 0,
      cost: 0,
      isPreSelected: false,
      displayOrder: 0,
    });

  useEffect(() => {
    if (predefinedValueDetails && visible) {
      setInitialValuesForm({
        id: predefinedValueDetails?.Id,
        name: predefinedValueDetails?.Name,
        priceAdjustment: predefinedValueDetails?.PriceAdjustment,
        cost: predefinedValueDetails?.Cost,
        colorSquaresRgb: predefinedValueDetails?.ColorSquaresRgb ?? undefined,
        isPreSelected: predefinedValueDetails?.IsPreSelected,
        displayOrder: predefinedValueDetails?.DisplayOrder,
        locales:
          predefinedValueDetails?.Locales?.map((item) => {
            return {
              id: item.Id,
              languageId: item.LanguageId,
              localeKey: item.LocaleKey,
              localeValue: item.LocaleValue,
            };
          }) ?? [],
      });
    } else {
      setInitialValuesForm({
        name: '',
        priceAdjustment: 0,
        cost: 0,
        isPreSelected: false,
        displayOrder: 0,
        locales: [],
      });
    }
  }, [predefinedValueDetails, visible]);

  const handleSubmit = async (values: any, setSubmitting: any) => {
    try {
      if (!predefinedValueDetails) {
        await productAttributesService.createPredefinedValue({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          productAttributeId: id,
        });
      } else {
        await productAttributesService.editPredefinedValue({
          ...values,
          name: !MainUtils.isEmptyValue(
            getLocaleValue(values?.locales, LanguageCode.English, 'name'),
          )
            ? getLocaleValue(values?.locales, LanguageCode.English, 'name')
            : getLocaleValue(values?.locales, LanguageCode.Arabic, 'name'),
          productAttributeId: id,
        });
      }
      await onFinish();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValuesForm}
      validationSchema={schema(messages)}
      onSubmit={(values, { setSubmitting }: any) =>
        handleSubmit(values, setSubmitting)
      }
      validateOnChange={false}
      validateOnBlur={false}>
      {({ handleSubmit, isSubmitting }: any) => (
        <Form>
          <Modal
            className='add-info-modal'
            width={'700px'}
            open={visible}
            maskClosable={false}
            title={
              <>
                <div className='text-title'>
                  <span className='icon-title'>
                    <FaRegListAlt />
                  </span>
                  {predefinedValueDetails ? (
                    <IntlMessages id='productAttributes.productAttributesPredefinedValues.editValueTitleModal' />
                  ) : (
                    <IntlMessages id='productAttributes.productAttributesPredefinedValues.addValueTitleModal' />
                  )}
                </div>
              </>
            }
            onCancel={onCancel}
            footer={false}>
            <Row gutter={[0, 20]}>
              <Col span={24}>
                <Row justify='end'>
                  <Col>
                    <Button
                      icon={<BsCheckLg className='add-button-icon' />}
                      onClickAction={handleSubmit}
                      text={messages['common.save'].toString()}
                      type='primary'
                      size='small'
                      className='add-button'
                      loading={isSubmitting}
                    />
                  </Col>
                </Row>
              </Col>

              <CreateOrEditPredefinedValue
                productAttributeType={productAttributeType}
              />
            </Row>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default ProductAttributeModal;
